import { ILeaderboardRow } from 'fitbeat.gym-core';
import produce from 'immer';
import { UPDATE_LEADERBOARD_SUCCESS } from '../../actions';
import {
  CALL_CONNECTED,
  CALL_DISCONNECT_REQUEST,
  CALL_DISCONNECTED,
  CALL_FAILED,
  CALL_INITIATE_SUCCESS,
  CALL_INITIATING,
  CLASS_LIST_LOADED,
  DISABLE_TALKING_SUCCESS,
  ENABLE_TALKING_SUCCESS,
  PARTICIPANT_JOINED_CALL,
  PARTICIPANT_LEFT_CALL,
  TALKING_TOGGLE_REQUEST,
  TOGGLE_LOCAL_AUDIO_MUTED,
  TOGGLE_LOCAL_VIDEO_MUTED,
  USER_TOGGLE_VIDEO,
  VideoCallingActionTypes,
  SPEAKER_VOLUME_CHHANGED,
} from './actions';

export enum CallConnectionState {
  'connecting',
  'connected',
  'disconnected',
  'disconnecting',
}

export interface IGroupParticipant {
  agoraUid?: number;
  leaderboardRow: ILeaderboardRow;
  userProfileId: string;
  videoEnabled: boolean;
}

export interface IGroupParticipants {
  [key: string]: IGroupParticipant | undefined;
}

export interface IGroupCallState {
  connectionState: CallConnectionState;
  participants: IGroupParticipants;
  callError?: any;
  permissionError?: boolean;
  shouldDisconnect: boolean;
  localAudioIsMuted: boolean;
  localVideoIsMuted: boolean;
  talkingEnabled: boolean;
  enableTalkingLoading: boolean;
  settings: any;
}

const initialState: IGroupCallState = {
  connectionState: CallConnectionState.disconnected,
  participants: {},
  shouldDisconnect: false,
  localAudioIsMuted: false,
  localVideoIsMuted: false,
  enableTalkingLoading: false,
  talkingEnabled: true,
  settings: null,
};

export const groupCallReducer = produce(
  (draft: IGroupCallState = initialState, action: VideoCallingActionTypes | any): IGroupCallState => {
    switch (action.type) {
      case CALL_INITIATING:
        delete draft.callError;
        delete draft.permissionError;
        draft.connectionState = CallConnectionState.connecting;
        setupInitialParticipants(draft, action);
        break;
      case CALL_INITIATE_SUCCESS:
        delete draft.callError;
        draft.connectionState = CallConnectionState.connecting;
        draft.settings = action.payload.settings;
        break;
      case CALL_CONNECTED:
        draft.connectionState = CallConnectionState.connected;
        break;
      case CALL_DISCONNECT_REQUEST:
        if (draft.connectionState === CallConnectionState.connected ||
          draft.connectionState === CallConnectionState.connecting) {
          draft.connectionState = CallConnectionState.disconnecting;
          draft.shouldDisconnect = true;
          delete draft.callError;
        }
        break;
      case CALL_DISCONNECTED:
        draft = { ...initialState };
        break;
      case CALL_FAILED:
        draft = { ...initialState };
        draft.callError = action.payload.errorMessage;
        draft.permissionError = action.payload.permissionError;
        break;
      case PARTICIPANT_JOINED_CALL:
        getOrCreateParticipantIfNotExists(
          draft, action.payload.uid, action.payload.userIdString);
        break;
      case PARTICIPANT_LEFT_CALL:
        const existing = draft.participants[action.payload.userIdString];
        if (!existing) {
          break;
        }
        delete existing.agoraUid;
        break;
      case TOGGLE_LOCAL_AUDIO_MUTED:
        draft.localAudioIsMuted = !draft.localAudioIsMuted;
        break;
      case TOGGLE_LOCAL_VIDEO_MUTED:
        draft.localVideoIsMuted = !draft.localVideoIsMuted;
        break;
      case ENABLE_TALKING_SUCCESS:
        draft.talkingEnabled = true;
        draft.enableTalkingLoading = false;
        break;
      case DISABLE_TALKING_SUCCESS:
        draft.talkingEnabled = false;
        draft.enableTalkingLoading = false;
        break;
      case TALKING_TOGGLE_REQUEST:
        draft.enableTalkingLoading = true;
        break;
      case UPDATE_LEADERBOARD_SUCCESS:
        const { groupCallLeaderboardRows } = action.payload.leaderboardResponse;
        createOrUpdateParticipantsFromLeaderboardRows(groupCallLeaderboardRows, draft,
          action.payload.participantIdsToCreateOrUpdateFromLeaderboardRows);
        break;
      case SPEAKER_VOLUME_CHHANGED:
        if (!draft.settings || !draft.settings.isHost) {
          break;
        }

        Object.keys(draft.participants).forEach((key: string) => {
          draft.participants[key].isTalking = action.payload.speakerVolumeInfo.vad === 1;
        });
        break;
      case USER_TOGGLE_VIDEO:
        const userIds = Object.keys(draft.participants);
        for (const userId of userIds) {
          const participant = draft.participants[userId];
          if (participant && participant.agoraUid === action.payload.uid) {
            participant.videoEnabled = action.payload.enabled;
          }
        }
        break;
    }
    return draft;
  });

const getOrCreateParticipantIfNotExists = (
  draft: IGroupCallState,
  agoraUid: number,
  userProfileId: string,
) => {
  const existing = draft.participants[userProfileId];
  if (!existing) {
    return draft.participants[userProfileId] = {
      agoraUid,
      leaderboardRow: createEmptyLeaderBoardRow(),
      userProfileId,
      videoEnabled: true,
    };
  }
  existing.agoraUid = agoraUid;
  return existing as IGroupParticipant;
};

const createOrUpdateParticipantsFromLeaderboardRows = (
  groupCallLeaderboardRows: { [key: string]: ILeaderboardRow },
  draft: IGroupCallState,
  participantIdsToCreateOrUpdateFromLeaderboardRows: string[],
) => {
    participantIdsToCreateOrUpdateFromLeaderboardRows.map((userProfileId: string) => {
    const leaderboardRow: ILeaderboardRow = groupCallLeaderboardRows[userProfileId];
    const existing = draft.participants[userProfileId];
    if (!existing) {
      draft.participants[userProfileId] = {
        leaderboardRow,
        userProfileId,
        videoEnabled: true,
      };
      return;
    }
    existing.leaderboardRow = leaderboardRow;
  });
};

const setupInitialParticipants = (
  draft: IGroupCallState,
  action: any,
) => {
  if (action.payload.isStreamingWorkoutAndNotHost) {
    return;
  }
  draft.participants = {
    [action.payload.userProfileId]: {
      leaderboardRow: createEmptyLeaderBoardRow(),
      userProfileId: action.payload.userProfileId,
      videoConnected: true,
    },
  };
};

const createEmptyLeaderBoardRow = () => ({
  initials: '-',
  name: '-',
  effortPoints: '-',
  cardioOrResistance: 'Cardio',
});
